/*eslint-disable*/
import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Divider,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  alpha,
  useTheme,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleIcon from "@mui/icons-material/People";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EmailIcon from "@mui/icons-material/Email";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useDispatch, useSelector } from "react-redux";
import { FirebaseContext } from "common";
import * as XLSX from 'xlsx';

// Helper function to parse XML response
const parseLeadsFromXML = (xmlText) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
    const leadDetails = xmlDoc.getElementsByTagName("ListingLeadDetail");
    const leads = [];
    
    for (let i = 0; i < leadDetails.length; i++) {
      const lead = leadDetails[i];
      leads.push({
        leadId: lead.getElementsByTagName("LeadId")[0]?.textContent || "",
        date: lead.getElementsByTagName("Date")[0]?.textContent || "",
        branchId: lead.getElementsByTagName("BranchId")[0]?.textContent || "",
        uniqueListingId: lead.getElementsByTagName("UniqueListingId")[0]?.textContent || "",
        ppRef: lead.getElementsByTagName("PPRef")[0]?.textContent || "",
        fromEmail: lead.getElementsByTagName("FromEmail")[0]?.textContent || "",
        fromName: lead.getElementsByTagName("FromName")[0]?.textContent || "",
        fromContactNumber: lead.getElementsByTagName("FromContactNumber")[0]?.textContent || "",
        toEmail: lead.getElementsByTagName("ToEmail")[0]?.textContent || "",
        message: lead.getElementsByTagName("Message")[0]?.textContent || ""
      });
    }
    
    return leads;
  } catch (error) {
    console.error("Error parsing XML:", error);
    return [];
  }
};

const LeadsNotifications = () => {
  const { api } = useContext(FirebaseContext);
  const tokenManagementReducer = useSelector((state) => state.tokendata);
  const ppLeadsReducer = useSelector((state) => state.leaddata);
  const { fetchPPLeads } = api;
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [Leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const theme = useTheme();
  const [tokens, settokens] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Fetch on mount & date change
  useEffect(() => {
    const token = tokenManagementReducer.token?.token;
    if (!token) return;

    settokens(token);
    
    // Only fetch data on initial load or when date changes
    if (isInitialLoad || !ppLeadsReducer.ppleads) {
      const dateStr = selectedDate.toISOString();
      dispatch(fetchPPLeads(token, dateStr));
      setIsInitialLoad(false);
    }
  }, [dispatch, selectedDate, tokenManagementReducer]);

  // Update leads when Redux state changes
  useEffect(() => {
    if (ppLeadsReducer.ppleads) {
      let leads = [];
      
      if (typeof ppLeadsReducer.ppleads === 'string') {
        // Parse XML response
        leads = parseLeadsFromXML(ppLeadsReducer.ppleads);
      } else if (Array.isArray(ppLeadsReducer.ppleads)) {
        // Already an array
        leads = ppLeadsReducer.ppleads;
      }
      
      setLeads(leads);
    } else {
      setLeads([]);
    }
  }, [ppLeadsReducer.ppleads]);

  // Safe mapping functions
  const safeMap = (array, mapper) => Array.isArray(array) ? array.map(mapper) : [];
  const safeFilter = (array, filterFn) => Array.isArray(array) ? array.filter(filterFn) : [];

  // Stats with safe access
  const stats = {
    total: Leads?.length || 0,
    uniqueSenders: new Set(safeMap(Leads, (l) => l.fromEmail)).size,
    branches: new Set(safeMap(Leads, (l) => l.branchId)).size
  };

  // Filtering with safe access
  const filteredLeads = safeFilter(Leads, (lead) => {
    if (filter === "all") return true;
    if (filter === "uniqueSenders") {
      const firstOcc = Leads.find((l) => l.fromEmail === lead.fromEmail);
      return firstOcc?.leadId === lead.leadId;
    }
    if (filter === "branches") {
      const firstOcc = Leads.find((l) => l.branchId === lead.branchId);
      return firstOcc?.leadId === lead.leadId;
    }
    return true;
  });

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const truncateText = (text, maxLength = 100) =>
    text && text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;

  const handleRefresh = () => {
    const dateStr = selectedDate.toISOString();
    dispatch(fetchPPLeads(tokens, dateStr));
  };

  // Function to export leads to Excel
  const exportToExcel = (leadsToExport, fileName) => {
    // Prepare data for Excel
    const excelData = leadsToExport.map(lead => ({
      "Lead ID": lead.leadId,
      "Date": formatDate(lead.date),
      "Branch ID": lead.branchId,
      "Listing ID": lead.uniqueListingId,
      "Property Reference": lead.ppRef,
      "Sender Name": lead.fromName,
      "Sender Email": lead.fromEmail,
      "Sender Phone": lead.fromContactNumber,
      "Recipient Email": lead.toEmail,
      "Message": lead.message
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    
    // Generate Excel file
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExportAll = () => {
    exportToExcel(Leads, `all_leads_${selectedDate.toISOString().split('T')[0]}`);
    handleExportClose();
  };

  const handleExportFiltered = () => {
    exportToExcel(filteredLeads, `filtered_leads_${selectedDate.toISOString().split('T')[0]}`);
    handleExportClose();
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={1}
        sx={{
          bgcolor: "white",
          color: "text.primary",
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 4
        }}
      >
        <Toolbar sx={{ py: 2 }}>
          <Typography variant="h5" fontWeight="600" sx={{ flexGrow: 1 }}>
            Property Leads Dashboard
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            sx={{ mr: 2 }}
          >
            Export
          </Button>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    bgcolor: "white",
                    borderRadius: 2,
                    width: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
          <IconButton
            color="primary"
            onClick={handleRefresh}
            sx={{
              ml: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Export Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleExportClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={handleExportAll}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export All Leads</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportFiltered}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Filtered Leads</ListItemText>
        </MenuItem>
      </Menu>

      <Container maxWidth="xl" sx={{ pb: 8 }}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "white",
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center"
              }}
            >
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  p: 1.5,
                  borderRadius: 3,
                  mr: 2
                }}
              >
                <EmailIcon color="primary" />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="600">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Leads
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "white",
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center"
              }}
            >
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  p: 1.5,
                  borderRadius: 3,
                  mr: 2
                }}
              >
                <PeopleIcon color="info" />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="600">
                  {stats.uniqueSenders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique Senders
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "white",
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center"
              }}
            >
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  p: 1.5,
                  borderRadius: 3,
                  mr: 2
                }}
              >
                <ApartmentIcon color="success" />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="600">
                  {stats.branches}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Branches
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Filter Toggle */}
        
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 3,
            bgcolor: "white",
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between"
            }}
          >
            <Typography variant="h6" sx={{ mb: { xs: 2, sm: 0 } }}>
              Filter Leads
            </Typography>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
              color="primary"
              size="small"
            >
              <ToggleButton value="all" sx={{ textTransform: "none", px: 3 }}>
                All Leads
              </ToggleButton>
              <ToggleButton
                value="uniqueSenders"
                sx={{ textTransform: "none", px: 3 }}
              >
                Unique Senders
              </ToggleButton>
              <ToggleButton
                value="branches"
                sx={{ textTransform: "none", px: 3 }}
              >
                By Branches
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {/* Error */}
        {ppLeadsReducer.error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {ppLeadsReducer.error.msg}
          </Alert>
        )}

        {/* Loading */}
        {ppLeadsReducer.loading && (
          <Box display="flex" justifyContent="center" sx={{ my: 8 }}>
            <Box textAlign="center">
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading leads...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Leads List */}
        {!ppLeadsReducer.loading && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TrendingUpIcon color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Showing {filteredLeads.length}{" "}
                {filter !== "all" ? "filtered" : ""} leads
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {filteredLeads.map((lead, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3, pb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2
                        }}
                      >
                        <Typography 
                          variant="h6" 
                          fontWeight="600"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {lead.fromName || "Unknown"}
                        </Typography>
                        <Chip
                          label={`#${lead.leadId}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Date
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(lead.date)}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          From
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {lead.fromEmail}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {lead.fromContactNumber}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          To
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {lead.toEmail}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Property
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {lead.uniqueListingId} ({lead.ppRef})
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Message
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ 
                            fontStyle: "italic",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "3",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {truncateText(lead.message)}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          textTransform: "none"
                        }}
                      >
                        Contact
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        href={`https://www.privateproperty.co.za/${lead.ppRef}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          ml: 1
                        }}
                      >
                        View Listing
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {!ppLeadsReducer.loading && filteredLeads.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: "center",
              borderRadius: 3,
              bgcolor: "white",
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <EmailIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No leads found for this date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try selecting a different date or check back later
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default LeadsNotifications;